import { GOOGLE_MAPS_PUBLIC_KEY } from 'helpers/config'

declare global {
    namespace google {
        namespace maps {
            interface GeocoderResult {
                address_components: Array<{
                    types: string[]
                    long_name: string
                    short_name: string
                }>
                formatted_address: string
                postalAddress?: {locality?: string; postalCode?: string; regionCode?: string}
            }
        }
    }
}

export interface AddressLookupSuggestion {
    placeId: string
    description: string
    resolvedAddress?: AddressLookupResult
    geocoderResult?: google.maps.GeocoderResult
}

export interface AddressLookupResult {
    street: string
    street_number?: string
    postcode: string
    city: string
    region: string
    country: string
    country_code: string
    focus: string
    lat?: number
    lng?: number
}

let googleMapsLoaderPromise: Promise<void> | null = null

const getScriptLanguage = (language?: string): string => {
    if (language && language.trim()) {
        return language.trim()
    }

    if (typeof document !== 'undefined' && document.documentElement.lang) {
        return document.documentElement.lang
    }

    return 'en'
}

export const loadGoogleMaps = (language?: string): Promise<void> => {
    if (typeof window === 'undefined') {
        return Promise.reject(new Error('Google Maps can only be loaded in the browser'))
    }

    const w = window as unknown as { google?: { maps?: unknown } }
    if (w.google?.maps) {
        return Promise.resolve()
    }

    if (googleMapsLoaderPromise) {
        return googleMapsLoaderPromise
    }

    if (!GOOGLE_MAPS_PUBLIC_KEY) {
        return Promise.reject(new Error('Missing Google Maps API key. Set VITE_GOOGLE_MAPS_PUBLIC_KEY.'))
    }

    googleMapsLoaderPromise = new Promise((resolve, reject) => {
        const existingScript = document.getElementById('google-maps-script') as HTMLScriptElement | null

        const handleScriptLoad = () => {
            const w2 = window as unknown as { google?: { maps?: unknown } }
            if (w2.google?.maps) {
                resolve()
            } else {
                googleMapsLoaderPromise = null
                reject(new Error('Google Maps failed to initialize'))
            }
        }

        if (existingScript) {
            const s = existingScript as unknown as { readyState?: string }
            if (s.readyState === 'complete' || s.readyState === 'loaded') {
                handleScriptLoad()
            } else {
                existingScript.addEventListener('load', handleScriptLoad, { once: true })
                existingScript.addEventListener(
                    'error',
                    () => {
                        googleMapsLoaderPromise = null
                        reject(new Error('Google Maps script failed to load'))
                    },
                    {
                        once: true,
                    }
                )
            }
            return
        }

        const script = document.createElement('script')
        script.id = 'google-maps-script'
        script.async = true
        // Google recommends loading=async in the URL for best-practice bootstrap.
        // See https://goo.gle/js-api-loading
        script.src = `https://maps.googleapis.com/maps/api/js?key=${GOOGLE_MAPS_PUBLIC_KEY}&libraries=places&language=${encodeURIComponent(getScriptLanguage(language))}&loading=async`
        script.addEventListener('load', handleScriptLoad, { once: true })
        script.addEventListener(
            'error',
            () => {
                googleMapsLoaderPromise = null
                reject(new Error('Google Maps script failed to load'))
            },
            {
                once: true,
            }
        )
        document.body.appendChild(script)
    })

    return googleMapsLoaderPromise
}

export const searchGoogleAddresses = async (
    input: string,
    countryCode?: string | string[] | null,
    language?: string
): Promise<AddressLookupSuggestion[]> => {
    if (!input || input.trim().length < 2) {
        return []
    }

    try {
        await loadGoogleMaps(language)

        const google_obj = window as unknown as { google: any }
        const sessionToken = new google_obj.google.maps.places.AutocompleteSessionToken()

        const request: any = {
            input: input.trim(),
            sessionToken,
            // Prefer street-level results so city/region picks don't leave street/postal empty.
            includedPrimaryTypes: ['street_address', 'premise', 'subpremise', 'route'],
        }

        const requestedCountries = (Array.isArray(countryCode) ? countryCode : [countryCode])
            .map((country) => country?.trim().toUpperCase())
            .filter((country): country is string => Boolean(country))
        const allowedCountries = ['CH', 'LI']
        const normalizedCountries = requestedCountries.filter((country) => allowedCountries.includes(country))

        // Seller supports address suggestions only in Switzerland and Liechtenstein.
        // Other countries remain available for manual address entry.
        request.includedRegionCodes = normalizedCountries.length > 0 ? normalizedCountries : allowedCountries

        const { suggestions } = await google_obj.google.maps.places.AutocompleteSuggestion.fetchAutocompleteSuggestions(request)

        return Promise.all(suggestions.filter((suggestion: any) => suggestion.placePrediction).map(async (suggestion: any) => {
            const placeId = suggestion.placePrediction.placeId
            const description = normalizeAddressSpelling(suggestion.placePrediction.text.text)
            const details = await fetchPlaceAddressById(placeId, language)
            if (!details) return {placeId, description}
            const address = enrichAddressFromDescription(mapGeocoderResultToAddressLookupResult(details), description)
            const street = formatStreetLineFromAddress(address)
            const displayDescription = address.postcode && address.city && street
                ? [street, `${address.postcode} ${address.city}`, address.country].filter(Boolean).join(', ')
                : address.postcode && !description.includes(address.postcode)
                    ? `${description} (${address.postcode})`
                    : description
            return {placeId, description: displayDescription, resolvedAddress: address, geocoderResult: details}
        }))
    } catch (error) {
        console.error('Google Places autocomplete error:', error)
        return []
    }
}

export const normalizeAddressSpelling = (value: string): string => value.replace(/ß/g, 'ss').replace(/ẞ/g, 'SS')

export const formatStreetLineFromAddress = (address: AddressLookupResult): string => {
    const parsedStreet = parseAddressFromDescription(address.focus).street
    // Some Places results incorrectly put the full formatted address into `route`.
    // Keep only the street part so town, canton and country stay in their own fields.
    const street = address.street.includes(',') && parsedStreet ? parsedStreet : address.street
    const composed = [street, address.street_number].filter(Boolean).join(' ')
    if (composed) {
        return normalizeAddressSpelling(composed)
    }

    if (
        parsedStreet &&
        !isSamePlaceName(parsedStreet, address.city) &&
        !isSamePlaceName(parsedStreet, address.region)
    ) {
        return normalizeAddressSpelling(parsedStreet)
    }

    return ''
}

const POSTCODE_CITY_PATTERN = /^(\d[\d\s-]{2,})\s+(.+)$/
const STANDALONE_POSTCODE_PATTERN = /^\d{4,6}$/
const looksLikePostcode = (value: string): boolean => STANDALONE_POSTCODE_PATTERN.test(value.trim())

const isSamePlaceName = (left?: string, right?: string): boolean => {
    if (!left?.trim() || !right?.trim()) {
        return false
    }

    return left.trim().toLowerCase() === right.trim().toLowerCase()
}

const emptyAddressLookup = (focus = ''): AddressLookupResult => ({
    street: '',
    street_number: undefined,
    postcode: '',
    city: '',
    region: '',
    country: '',
    country_code: '',
    focus,
})

/**
 * Google autocomplete often returns Swiss lines as:
 * - "Street, 9015 St. Gallen, Switzerland"
 * - "Street, St. Gallen, 9015, Switzerland" (PLZ as its own segment — easy to mis-read as region)
 * - "Street, St. Gallen, Switzerland" (no PLZ in the suggestion text)
 */
export const parseAddressFromDescription = (description: string): AddressLookupResult => {
    description = normalizeAddressSpelling(description)
    const parts = description
        .split(',')
        .map((part) => part.trim())
        .filter(Boolean)

    if (parts.length === 0) {
        return emptyAddressLookup(description)
    }

    const lastPart = parts[parts.length - 1]
    const hasCountry = parts.length >= 2 && !looksLikePostcode(lastPart) && !POSTCODE_CITY_PATTERN.test(lastPart)
    const country = hasCountry ? lastPart : ''
    const middle = hasCountry ? parts.slice(0, -1) : parts

    const postcodeCityIndex = middle.findIndex((part) => POSTCODE_CITY_PATTERN.test(part))
    if (postcodeCityIndex >= 0) {
        const postcodeMatch = middle[postcodeCityIndex].match(POSTCODE_CITY_PATTERN)
        const postcode = postcodeMatch?.[1]?.replace(/\s+/g, ' ').trim() ?? ''
        const city = postcodeMatch?.[2]?.trim() ?? ''
        const street = middle.slice(0, postcodeCityIndex).join(', ')
        const after = middle.slice(postcodeCityIndex + 1).filter((part) => !looksLikePostcode(part))
        const region = after[after.length - 1] || ''

        return {
            ...emptyAddressLookup(description),
            street,
            postcode,
            city,
            region,
            country,
        }
    }

    const standalonePostcodeIndex = middle.findIndex((part) => looksLikePostcode(part))
    if (standalonePostcodeIndex >= 0) {
        const postcode = middle[standalonePostcodeIndex]
        const withoutPostcode = middle.filter((_, index) => index !== standalonePostcodeIndex)

        // "Street, City, 9015" or "Street, 9015, City"
        if (withoutPostcode.length >= 2) {
            return {
                ...emptyAddressLookup(description),
                street: withoutPostcode[0],
                city: withoutPostcode[1],
                region: withoutPostcode.length >= 3 ? withoutPostcode[withoutPostcode.length - 1] : '',
                postcode,
                country,
            }
        }

        if (withoutPostcode.length === 1) {
            const only = withoutPostcode[0]
            const isStreetLike = /\d/.test(only) || /strasse|straße|weg|gasse|place|street|avenue/i.test(only)
            return {
                ...emptyAddressLookup(description),
                street: isStreetLike ? only : '',
                city: isStreetLike ? '' : only,
                postcode,
                country,
            }
        }

        return {
            ...emptyAddressLookup(description),
            postcode,
            country,
        }
    }

    // No PLZ segment: "Street, City, Country" or "Street, City, Region, Country"
    if (middle.length >= 2) {
        return {
            ...emptyAddressLookup(description),
            street: middle[0],
            city: middle[1],
            region: middle.length >= 3 ? middle[middle.length - 1] : '',
            country,
        }
    }

    return {
        ...emptyAddressLookup(description),
        city: middle[0] || '',
        country,
    }
}

export const enrichAddressFromDescription = (
    address: AddressLookupResult,
    description: string
): AddressLookupResult => {
    const fallback = parseAddressFromDescription(description)
    const focusFallback = address.focus && address.focus !== description
        ? parseAddressFromDescription(address.focus)
        : emptyAddressLookup()

    const regionCandidate = address.region || fallback.region || focusFallback.region
    const postcodeCandidate = address.postcode || fallback.postcode || focusFallback.postcode

    // If region was wrongly filled with a PLZ, move it to postcode.
    const regionLooksLikePostcode = regionCandidate ? looksLikePostcode(regionCandidate) : false
    const postcode = postcodeCandidate || (regionLooksLikePostcode ? regionCandidate : '')
    const region = regionLooksLikePostcode ? '' : regionCandidate
    const city = address.city || fallback.city || focusFallback.city

    // "City, Region, Country" descriptions parse the city as street — reject that when
    // components already identified the city (common for locality-level Places results).
    const streetCandidate = address.street || fallback.street || focusFallback.street
    const street =
        streetCandidate &&
        !isSamePlaceName(streetCandidate, city) &&
        !isSamePlaceName(streetCandidate, region)
            ? streetCandidate
            : address.street || ''

    return {
        ...address,
        street,
        street_number: address.street_number,
        postcode,
        city,
        region,
        country: address.country || fallback.country || focusFallback.country,
        country_code: address.country_code || fallback.country_code || focusFallback.country_code,
        focus: address.focus || description,
    }
}

export const resolvePlaceAddress = async (
    placeId: string,
    description: string,
    language?: string
): Promise<AddressLookupResult> => {
    try {
        // Prefer Places API (New) details — same API family as AutocompleteSuggestion.
        // Avoid Geocoding API: restricted keys often allow Places but block Geocoding
        // (ApiTargetBlockedMapError).
        const placeDetails = await fetchPlaceAddressById(placeId, language)
        const mapped = placeDetails
            ? mapGeocoderResultToAddressLookupResult(placeDetails)
            : parseAddressFromDescription(description)

        return enrichAddressFromDescription(mapped, description)
    } catch (error) {
        console.warn('resolvePlaceAddress failed, using suggestion text:', error)
        return parseAddressFromDescription(description)
    }
}

/**
 * Resolve structured address components via Places API (New) Place.fetchFields.
 * Returns null when Places is unavailable; callers should fall back to description parsing.
 */
const placeAddressCache = new Map<string, Promise<google.maps.GeocoderResult | null>>()

export const fetchPlaceAddressById = (
    placeId: string,
    language?: string
): Promise<google.maps.GeocoderResult | null> => {
    if (!placeId) return Promise.resolve(null)
    const key = `${language || ''}:${placeId}`
    const cached = placeAddressCache.get(key)
    if (cached) return cached
    let timer: ReturnType<typeof setTimeout> | undefined
    const pending = Promise.race([
        fetchPlaceAddressDetails(placeId, language),
        new Promise<null>(resolve => { timer = setTimeout(() => resolve(null), 4000) }),
    ]).finally(() => clearTimeout(timer)).then(result => {
        if (!result && placeAddressCache.get(key) === pending) placeAddressCache.delete(key)
        return result
    })
    // Keep autocomplete details in memory only, bounded to avoid growing with every search.
    if (placeAddressCache.size >= 100) placeAddressCache.delete(placeAddressCache.keys().next().value!)
    placeAddressCache.set(key, pending)
    return pending
}

const fetchPlaceAddressDetails = async (
    placeId: string,
    language?: string
): Promise<google.maps.GeocoderResult | null> => {
    if (!placeId) {
        return null
    }

    try {
        await loadGoogleMaps(language)

        const googleMaps = (window as unknown as { google: { maps: any } }).google.maps
        const place = new googleMaps.places.Place({ id: placeId })

        await place.fetchFields({
            fields: ['addressComponents', 'formattedAddress', 'location', 'postalAddress'],
        })

        const rawComponents = Array.isArray(place.addressComponents) ? place.addressComponents : []
        if (rawComponents.length === 0 && !place.formattedAddress) {
            return null
        }

        const lat =
            typeof place.location?.lat === 'function'
                ? place.location.lat()
                : place.location?.lat ?? 0
        const lng =
            typeof place.location?.lng === 'function'
                ? place.location.lng()
                : place.location?.lng ?? 0

        return {
            address_components: normalizeAddressComponents(
                rawComponents.map((component: RawAddressComponent) => ({
                    longText: component.longText,
                    shortText: component.shortText,
                    long_name: component.long_name,
                    short_name: component.short_name,
                    types: normalizeComponentTypes(component.types),
                }))
            ),
            formatted_address: place.formattedAddress || '',
            postalAddress: place.postalAddress,
            geometry: {
                location: {
                    lat: () => lat,
                    lng: () => lng,
                },
            },
        } as google.maps.GeocoderResult
    } catch (error) {
        console.warn('Places place details lookup failed:', error)
        return null
    }
}

/** @deprecated Prefer fetchPlaceAddressById — kept for callers/tests that still import this name. */
export const geocodePlaceById = fetchPlaceAddressById

type RawAddressComponent = {
    long_name?: string
    short_name?: string
    longText?: string
    shortText?: string
    types?: Array<string | { type?: string; toString?: () => string }>
}

const normalizeComponentTypes = (
    types: RawAddressComponent['types']
): string[] =>
    (types ?? [])
        .map((type) => {
            if (typeof type === 'string') {
                return type
            }
            if (type && typeof type === 'object') {
                if (typeof type.type === 'string') {
                    return type.type
                }
                if (typeof type.toString === 'function') {
                    const asString = type.toString()
                    if (asString && asString !== '[object Object]') {
                        return asString
                    }
                }
            }
            return ''
        })
        .filter(Boolean)

const normalizeAddressComponent = (component: RawAddressComponent) => ({
    long_name: component.longText ?? component.long_name ?? '',
    short_name: component.shortText ?? component.short_name ?? '',
    types: normalizeComponentTypes(component.types),
})

const normalizeAddressComponents = (
    components: RawAddressComponent[] | undefined
): google.maps.GeocoderResult['address_components'] =>
    (components ?? []).map(normalizeAddressComponent)

const getAddressComponent = (
    result: google.maps.GeocoderResult,
    type: string,
    shortName: boolean = false
): string => {
    const component = result.address_components.find((item) =>
        normalizeComponentTypes(item.types).includes(type)
    ) as RawAddressComponent | undefined
    if (!component) {
        return ''
    }

    if (shortName) {
        return component.short_name ?? component.shortText ?? ''
    }

    return component.long_name ?? component.longText ?? ''
}

const resolveStreetFromComponents = (result: google.maps.GeocoderResult): string => {
    const route = getAddressComponent(result, 'route')
    if (route) {
        return route
    }

    // Some international / building results expose premise instead of route.
    return (
        getAddressComponent(result, 'premise') ||
        getAddressComponent(result, 'subpremise') ||
        ''
    )
}

export const mapGeocoderResultToAddressLookupResult = (
    result: google.maps.GeocoderResult
): AddressLookupResult => {
    const street = normalizeAddressSpelling(resolveStreetFromComponents(result))
    const streetNumber = getAddressComponent(result, 'street_number')
    const city =
        result.postalAddress?.locality ||
        getAddressComponent(result, 'postal_town') ||
        getAddressComponent(result, 'locality') ||
        getAddressComponent(result, 'administrative_area_level_3') ||
        getAddressComponent(result, 'sublocality') ||
        getAddressComponent(result, 'administrative_area_level_2')
    // Prefer short code (SG); fall back to long name (St. Gallen / Sankt Gallen).
    const region =
        getAddressComponent(result, 'administrative_area_level_1', true) ||
        getAddressComponent(result, 'administrative_area_level_1')
    const postcode = result.postalAddress?.postalCode || getAddressComponent(result, 'postal_code')
    const country = getAddressComponent(result, 'country')
    const countryCode = result.postalAddress?.regionCode || getAddressComponent(result, 'country', true)
    const location = result.geometry?.location

    const mapped: AddressLookupResult = {
        street,
        street_number: streetNumber || undefined,
        postcode,
        city,
        region,
        country,
        country_code: countryCode,
        focus: normalizeAddressSpelling(result.formatted_address),
        lat: location?.lat(),
        lng: location?.lng(),
    }

    // formatted_address often has "9015 St. Gallen" even when components are sparse
    return enrichAddressFromDescription(mapped, result.formatted_address || '')
}
